import { LitElement, html, css } from 'lit';
import { FetchMixin } from '../FetchMixin';

class DashboardGraph extends FetchMixin(LitElement) {
    static get styles() {
        return css`
        :host {
            display: flex;
            flex-direction: column;
            padding: 16px;
            padding-bottom: 0;
            user-select: none;
        }

        .section {
            background: #ffffff;
            border-radius: 12px;
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
            border: 1px solid #f3f4f6;
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%; 
            box-sizing: border-box; 
            padding-bottom: 20px; 
            margin-bottom: 24px;
        }

        h1 {
            font-size: 1.125rem;
            font-weight: 700;
            color: #111827;
            margin: 0; /* マージンをリセット */
            text-decoration-line: none;
            white-space: nowrap; /* 折り返しを防止 */
        }

        .graph-title {
            display: flex;
            align-items: center;
            justify-content: space-between; /* 左右に分散配置 */
            width: 100%;
            padding: 20px 20px 10px 20px; /* DashboardPointと統一 */
            box-sizing: border-box;
            position: relative;
        }
        
        .graph-actions {
            display: flex;
            gap: 8px; /* ボタン間隔を調整 */
        }

        .graph-title button {
            font-size: 14px;
            border: none;
            border-radius: 50%;
            background-color: #f3f4f6;
            color: #4b5563;
            cursor: pointer;
            transition: all 0.2s ease;
            width: 32px;
            height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }

        .graph-title button:disabled {
            background-color: #f3f4f6;
            color: #9ca3af;
            cursor: not-allowed;
            opacity: 0.7;
        }

        .graph-title button:not(:disabled):hover {
            background-color: #e5e7eb;
            color: #111827;
        }

        @keyframes rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .rotate-animation { animation: rotate 2s linear infinite; }

        .chart-container {
            position: relative;
            width: 200px;
            height: 200px;
            margin-top: 10px;
        }

        .chart-text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            text-align: center;
            font-size: 13px;
            color: #6b7280;
            width: 200px;
        }

        .used-text {
            font-size: 28px;
            font-weight: 800;
            color: #111827;
            margin: 4px 0;
        }

        .percentage-high { color: #ef4444; }
        `;
    }

    static get properties() {
        return {
            totalDiskSpace: { type: Number },
            usedDiskSpace: { type: Number }
        };
    }

    constructor() {
        super();
        this.totalDiskSpace = 20;
        this.usedDiskSpace = 0.0;

        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') this.showUsedDiskSpace();
        })
    }

    updated(changedProperties) {
        if (changedProperties.has('totalDiskSpace') || changedProperties.has('usedDiskSpace')) {
            this._drawProgressCircle();
        }
    }

    firstUpdated(changedProperties) {
        requestAnimationFrame(() => {
            const fontAwasomeStyle = document.querySelector("link[rel='stylesheet'][data-name='fontawesomev6']");
            if (fontAwasomeStyle) {
                const fontAwasomeStyleClone = fontAwasomeStyle.cloneNode(true);
                fontAwasomeStyleClone.removeAttribute('disabled');
                this.shadowRoot.insertBefore(fontAwasomeStyleClone, this.shadowRoot.firstChild);
            }
        })
    }

    showUsedDiskSpace() {
        const loginCookieName = process.env.LOGIN_COOKIE_NAME;
        const isLogin = document.cookie.split(';').some((item) => item.trim().startsWith(loginCookieName + '='));
        if (isLogin) {
            requestAnimationFrame(async () => {
                const reloadBtn = this.shadowRoot.querySelector('#reload-button');
                const rotIcon = this.shadowRoot.querySelector('#rotete-icon');
                if (reloadBtn) reloadBtn.disabled = true;
                if (rotIcon) rotIcon.classList.add('rotate-animation');

                try {
                    await this.fetchUsedDiskSpace();
                } catch (error) {
                    console.error('Error fetching used disk space:', error);
                } finally {
                    if (rotIcon) rotIcon.classList.remove('rotate-animation');
                    if (reloadBtn) reloadBtn.disabled = false;
                }
            })
        }
    }

    async fetchUsedDiskSpace() {
        try {
            const json = await this.fetchWithAuth(process.env.USED_DISK_SPACE_ENDPOINT, { method: 'GET' });
            this.totalDiskSpace = json.details.totalDiskSpace;
            this.usedDiskSpace = json.details.usedDiskSpace;
            return Promise.resolve(json);
        } catch (error) {
            return Promise.reject(error);
        }
    }

    _drawProgressCircle() {
        var canvas = this.shadowRoot.getElementById('diskChart');
        if (!canvas) return;
        var ctx = canvas.getContext('2d');
        var usedPercentage = (this.usedDiskSpace / this.totalDiskSpace) * 100;
        var endAngle = (usedPercentage / 100) * Math.PI * 2;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 背景サークル
        ctx.beginPath();
        ctx.arc(100, 100, 80, 0, Math.PI * 2);
        ctx.lineWidth = 8;
        ctx.strokeStyle = '#f3f4f6'; // Gray 100
        ctx.stroke();

        // 使用量サークル
        ctx.beginPath();
        ctx.arc(100, 100, 80, -0.5 * Math.PI, endAngle - 0.5 * Math.PI);
        ctx.lineWidth = 8;
        ctx.strokeStyle = usedPercentage > 80 ? '#ef4444' : '#3b82f6'; // Red 500 or Blue 500
        ctx.lineCap = 'round';
        ctx.stroke();
    }

    render() {
        const usedPercentage = Math.round((this.usedDiskSpace / this.totalDiskSpace) * 100);
        const percentageClass = usedPercentage > 80 ? 'percentage-high' : '';

        return html`
            <div class="section">
                <div class="graph-title">
                    <h1>ディスク使用量</h1>
                    <div class="graph-actions">
                        <button id="reload-button" @click="${this.showUsedDiskSpace}" title="更新">
                            <i id="rotete-icon" class="fa-solid fa-rotate"></i>
                        </button>
                    </div>
                </div>
                <div class="chart-container">  
                    <canvas id="diskChart" width="200" height="200"></canvas>
                    <div class="chart-text">
                        <div style="font-size:11px; margin-bottom:2px;">使用済み</div>
                        <div class="used-text ${percentageClass}">
                            ${usedPercentage}%
                        </div>
                        <div style="font-size:11px;">${this.usedDiskSpace}MB / ${this.totalDiskSpace}MB</div>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('dashboard-graph', DashboardGraph);